/**
 * "My Games" Service
 *
 * Cross-origin cookie-authed against ac-mvp. Builds the landing-page
 * game picker from existing prod endpoints:
 *
 *   GET /api/games/my-games          — every game the user is in (GM or
 *                                       player), with derived vttEnabled
 *                                       + imagery. The roster source of
 *                                       truth — membership does NOT
 *                                       depend on having a session.
 *   GET /api/games/my-gm-games       — game ids the user GMs (role tag)
 *   GET /api/sessions?status=…       — upcoming + active sessions, used
 *                                       only to enrich rows with launch
 *                                       targets
 *   POST /api/vtt/launch             — returns the launch URL (JWT)
 *
 * Role per game is determined by set membership in the GM list;
 * anything not in that set the user is a player on. No "who am I"
 * endpoint needed.
 */

export interface MyGameSummary {
  gameId: string
  title: string
  role: 'gm' | 'player'
  playerCount: number
  /** Next upcoming SCHEDULED session at or after now. Null if the
   *  game has nothing scheduled. */
  nextSession: {
    id: string
    scheduledTime: string | null
    sessionNumber: number | null
  } | null
  /** First currently-ACTIVE session. Preferred launch target — players
   *  follow what the GM is running, not the calendar order. */
  liveSession: { id: string } | null
  /** Mirrors the check `GMToolsGameCard.tsx` uses to decide whether
   *  the game is configured for AC VTT: there must be a CampaignVTT
   *  row AND `Game.acVttEnabled !== false`. Defaults to `true` if the
   *  server response omits the field (older ac-mvp deployments
   *  without the VTT-enabled flag plumbing — keeps the picker
   *  permissive until the platform PR lands). */
  vttEnabled: boolean
  /** Primary banner — the image used everywhere else on the platform
   *  for this game (`/api/bookings/me`, `/api/player-games`,
   *  `/gms/[id]`). Null when the campaign hasn't set one; the landing
   *  renders a deterministic solid-color tile instead of a placeholder
   *  image. */
  gameImage: string | null
  /** Secondary hero treatment, reserved for a future detail/lobby view.
   *  Surfaced here so a card can opt to use it when both are present. */
  heroImage: string | null
}

export type MyGamesFetchResult =
  | { ok: true; games: MyGameSummary[] }
  | { ok: false; status: number; error: string }

function getPlatformUrl(): string {
  return import.meta.env.VITE_MAIN_PLATFORM_URL || 'https://arcanecircle.games'
}

type ApiSession = {
  id: string
  gameId: string
  scheduledTime: string | null
  sessionNumber: number | null
  status: string
  game: {
    id: string
    title: string
    gmId: string
    /** Added by the platform PR that pairs with this one. Treat as
     *  `true` when absent so older deployments stay launchable. */
    vttEnabled?: boolean
    gameImage?: string | null
    heroImage?: string | null
  }
  _count?: { bookings?: number }
}

type GmGamesResponse = {
  games: Array<{
    id: string
    title: string
    /** See `MyGameSummary.vttEnabled`. */
    vttEnabled?: boolean
    gameImage?: string | null
    heroImage?: string | null
  }>
}
/** `/api/games/my-games` — every game the user is in (GM via `gmId`, or
 *  player via a booking). The source of truth for the picker's roster:
 *  a game appears here whether or not it has an upcoming session. The
 *  `vttEnabled` flag is derived server-side (CampaignVTT row exists AND
 *  `acVttEnabled !== false`). Optional for backwards-compat with
 *  deployments that don't yet plumb it (treated as `true`). */
type MyGamesResponse = {
  games: Array<{
    id: string
    title: string
    system?: string
    vttEnabled?: boolean
    gameImage?: string | null
    heroImage?: string | null
  }>
}
type SessionsResponse = { sessions: ApiSession[] }

async function fetchJson<T>(
  platformUrl: string,
  path: string,
): Promise<{ ok: true; data: T } | { ok: false; status: number; error: string }> {
  try {
    const res = await fetch(`${platformUrl}${path}`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
    if (res.status === 401) {
      return { ok: false, status: 401, error: 'Not signed in' }
    }
    if (!res.ok) {
      return {
        ok: false,
        status: res.status,
        error: `Server returned ${res.status}`,
      }
    }
    const data = (await res.json()) as T
    return { ok: true, data }
  } catch (error) {
    return {
      ok: false,
      status: 0,
      error: error instanceof Error ? error.message : 'Network error',
    }
  }
}

export async function fetchMyGames(): Promise<MyGamesFetchResult> {
  const platformUrl = getPlatformUrl()
  const nowIso = new Date().toISOString()

  const [myGames, gmGames, scheduled, active] = await Promise.all([
    fetchJson<MyGamesResponse>(platformUrl, '/api/games/my-games'),
    fetchJson<GmGamesResponse>(platformUrl, '/api/games/my-gm-games'),
    fetchJson<SessionsResponse>(
      platformUrl,
      `/api/sessions?status=SCHEDULED&dateFrom=${encodeURIComponent(nowIso)}&limit=100`,
    ),
    fetchJson<SessionsResponse>(platformUrl, '/api/sessions?status=ACTIVE&limit=100'),
  ])

  // Any 401 means not authenticated — surface that uniformly.
  if (!myGames.ok && myGames.status === 401) return myGames
  if (!gmGames.ok && gmGames.status === 401) return gmGames
  if (!scheduled.ok && scheduled.status === 401) return scheduled
  if (!active.ok && active.status === 401) return active

  // Otherwise, treat any failure as a network error (single message).
  const firstFailure = [myGames, gmGames, scheduled, active].find((r) => !r.ok)
  if (firstFailure && !firstFailure.ok) return firstFailure

  // Narrow: all four are ok at this point.
  if (!myGames.ok || !gmGames.ok || !scheduled.ok || !active.ok) {
    return { ok: false, status: 500, error: 'Unexpected fetch state' }
  }

  // `/api/games/my-gm-games` is the definitive GM-id set, used only to
  // tag each row's role. Imagery + the vttEnabled flag come from
  // `/api/games/my-games` below.
  const gmGameIds = new Set(gmGames.data.games.map((g) => g.id))

  // Seed the roster from `my-games` — every game the user is in,
  // whether or not it has an upcoming session. Sessions (below) only
  // enrich these rows with launch targets; they never decide
  // membership. Games without a session are still shown (the requested
  // behavior); their launch button is simply disabled.
  const byGame = new Map<string, MyGameSummary>()
  for (const g of myGames.data.games) {
    byGame.set(g.id, {
      gameId: g.id,
      title: g.title,
      role: gmGameIds.has(g.id) ? 'gm' : 'player',
      playerCount: 0,
      nextSession: null,
      liveSession: null,
      vttEnabled: g.vttEnabled ?? true,
      gameImage: g.gameImage ?? null,
      heroImage: g.heroImage ?? null,
    })
  }

  // Enrich the seeded rows with live/upcoming sessions. Keep the
  // earliest scheduled-time future session per game and flag
  // liveSession when an ACTIVE session exists for the same game.
  const upsert = (s: ApiSession, isLive: boolean) => {
    let existing = byGame.get(s.gameId)
    if (!existing) {
      // Defensive: a session referencing a game `my-games` didn't
      // return (roster lag / access edge case). Insert a minimal row
      // from the session payload so the game isn't lost.
      existing = {
        gameId: s.gameId,
        title: s.game.title,
        role: gmGameIds.has(s.gameId) ? 'gm' : 'player',
        playerCount: 0,
        nextSession: null,
        liveSession: null,
        vttEnabled: s.game.vttEnabled ?? true,
        gameImage: s.game.gameImage ?? null,
        heroImage: s.game.heroImage ?? null,
      }
      byGame.set(s.gameId, existing)
    }
    if (s._count?.bookings != null) {
      existing.playerCount = Math.max(existing.playerCount, s._count.bookings)
    }
    // Backfill imagery from the session payload when `my-games` didn't
    // return it. This keeps prod tile artwork rendering against ac-mvp
    // deployments that haven't yet plumbed gameImage/heroImage through
    // `/api/games/my-games` (see ac-mvp #251). Sessions endpoint has
    // carried these fields for a while.
    if (existing.gameImage == null && s.game.gameImage) {
      existing.gameImage = s.game.gameImage
    }
    if (existing.heroImage == null && s.game.heroImage) {
      existing.heroImage = s.game.heroImage
    }
    if (isLive) {
      if (!existing.liveSession) existing.liveSession = { id: s.id }
      return
    }
    if (s.scheduledTime) {
      const existingTime = existing.nextSession?.scheduledTime
      if (
        !existingTime ||
        new Date(s.scheduledTime).getTime() < new Date(existingTime).getTime()
      ) {
        existing.nextSession = {
          id: s.id,
          scheduledTime: s.scheduledTime,
          sessionNumber: s.sessionNumber,
        }
      }
    }
  }

  for (const s of active.data.sessions) upsert(s, true)
  for (const s of scheduled.data.sessions) upsert(s, false)

  const games = Array.from(byGame.values())
    // Games whose campaign has opted out of (or never opted into) the
    // AC VTT — `vttEnabled` is derived server-side on `my-games` — are
    // still surfaced, but rendered greyed-out and unlaunchable (their
    // GMs are using Foundry / Roll20 / etc.). Sort them to the bottom
    // so the launchable games lead.
    .sort((a, b) => {
      if (a.vttEnabled && !b.vttEnabled) return -1
      if (b.vttEnabled && !a.vttEnabled) return 1
      if (a.liveSession && !b.liveSession) return -1
      if (b.liveSession && !a.liveSession) return 1
      const at = a.nextSession?.scheduledTime ?? null
      const bt = b.nextSession?.scheduledTime ?? null
      if (at && bt) return new Date(at).getTime() - new Date(bt).getTime()
      if (at) return -1
      if (bt) return 1
      return 0
    })

  return { ok: true, games }
}

/**
 * Cross-origin access bucket served by ac-mvp's `/api/me/tier`.
 *
 * `tier` is the user's highest qualifying bucket; `vttAllowed` is
 * the derived gate (true for everything except `free`). The raw
 * signals are passed through so the landing's blocked state can be
 * specific ("you're on the wait-list" vs. "your subscription
 * expired" vs. "sign in to continue").
 */
export interface MyTier {
  tier: 'admin' | 'alpha' | 'premium' | 'free'
  isAdmin: boolean
  accessTier: string | null
  subscriptionTier: string | null
  vttAllowed: boolean
}

export type MyTierFetchResult =
  | { ok: true; tier: MyTier }
  | { ok: false; status: number; error: string }

export async function fetchMyTier(): Promise<MyTierFetchResult> {
  const platformUrl = getPlatformUrl()
  const res = await fetchJson<MyTier>(platformUrl, '/api/me/tier')
  if (!res.ok) return res
  return { ok: true, tier: res.data }
}

/**
 * POST to `/api/vtt/launch` with a sessionId; navigate to the returned
 * URL (which embeds the VTT JWT) so the existing in-app auth flow
 * picks it up. Cross-origin POST — credentials included for cookie auth.
 */
export async function launchVTTSession(sessionId: string): Promise<
  { ok: true; url: string } | { ok: false; status: number; error: string }
> {
  const platformUrl = getPlatformUrl()
  try {
    const response = await fetch(`${platformUrl}/api/vtt/launch`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId }),
    })

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      return {
        ok: false,
        status: response.status,
        error: data.error || `Server returned ${response.status}`,
      }
    }

    const data = await response.json()
    if (typeof data.launchUrl !== 'string') {
      return {
        ok: false,
        status: 500,
        error: 'Launch URL missing from response',
      }
    }
    return { ok: true, url: data.launchUrl }
  } catch (error) {
    return {
      ok: false,
      status: 0,
      error: error instanceof Error ? error.message : 'Network error',
    }
  }
}
